import { useState, useEffect } from "react";
import axios from "axios";
import { fmtPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Paperclip, PackageSearch, ImageIcon, Search, Truck } from "lucide-react";

const KIND_LABELS = { 0: "Units", 1: "Accessories", 2: "Parts" };
const KIND_BADGE = { 0: "Unit", 1: "Accessory", 2: "Part" };
const KIND_BADGE_CLASS = {
  0: "bg-blue-100 text-blue-700",
  1: "bg-amber-100 text-amber-700",
  2: "bg-violet-100 text-violet-700",
};

function CatalogImg({ url }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) return;
    let obj, active = true;
    axios.get(url, { responseType: "blob" })
      .then((r) => { if (active) { obj = URL.createObjectURL(r.data); setSrc(obj); } })
      .catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  if (!url) return <div className="w-20 h-20 rounded-sm bg-slate-100 grid place-items-center text-slate-300 shrink-0"><ImageIcon className="h-6 w-6" /></div>;
  if (!src) return <div className="w-20 h-20 rounded-sm bg-slate-100 animate-pulse shrink-0" />;
  return <img src={src} alt="" className="w-20 h-20 rounded-sm object-cover border border-slate-200 shrink-0" />;
}

export function ShippingNotice({ shipping }) {
  if (!shipping) return null;
  if (shipping.mode === "free") {
    return (
      <div className="rounded-sm bg-[#12B0EA] text-slate-900 px-6 py-6 mb-8" data-testid="shipping-notice-free">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em]"><Truck className="h-4 w-4" /> {shipping.eyebrow}</div>
        <div className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1">{shipping.title}</div>
        <div className="text-sm sm:text-base mt-1 whitespace-pre-line">{shipping.text}</div>
      </div>
    );
  }
  return (
    <div className="rounded-sm border border-slate-200 bg-white overflow-hidden mb-8" data-testid="shipping-notice-tiers">
      <div className="bg-slate-900 text-white px-5 py-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em]"><Truck className="h-4 w-4" /> Shipping · {shipping.title}</div>
      <div className="grid sm:[grid-template-columns:var(--cols)] divide-y sm:divide-y-0 sm:divide-x divide-slate-100" style={{ "--cols": `repeat(${shipping.tiers.length || 1}, minmax(0, 1fr))` }}>
        {shipping.tiers.map((t) => (
          <div key={t.label} className="flex sm:flex-col items-center sm:items-start justify-between px-5 py-3" data-testid={`shipping-tier-${t.label.replace(/\s+/g, "-")}`}>
            <span className="text-sm text-slate-600">{t.label}</span>
            <span className="font-heading text-2xl font-black text-slate-900 mono-nums">${Number(t.amount) % 1 === 0 ? Number(t.amount) : Number(t.amount).toFixed(2)}</span>
          </div>
        ))}
      </div>
      {shipping.text ? <div className="px-5 py-2 text-xs text-slate-500 border-t border-slate-100 whitespace-pre-line">{shipping.text}</div> : null}
    </div>
  );
}

function ProductRow({ item, fileUrl }) {
  const discounted = item.list_price != null && item.list_price > item.price;
  return (
    <div className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors" data-testid={`product-row-${item.sku}`}>
      <CatalogImg url={item.image_ref ? fileUrl(item.image_ref) : null} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-slate-500">{item.sku}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm shrink-0 ${KIND_BADGE_CLASS[item.kind] || KIND_BADGE_CLASS[0]}`}
            title="Shipping category" data-testid={`kind-badge-${item.sku}`}>
            {KIND_BADGE[item.kind] ?? "Unit"}
          </span>
        </div>
        <div className="font-medium text-slate-900 whitespace-normal break-words" data-testid={`description-${item.sku}`}>{item.description || "—"}</div>
        {item.notes ? <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-line break-words" data-testid={`notes-${item.sku}`}>{item.notes}</div> : null}
        {item.attachments.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-1.5">
            {item.attachments.map((a) => (
              <a key={a.ref_id} href={fileUrl(a.ref_id)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors" data-testid={`attachment-${a.ref_id}`}>
                <Paperclip className="h-3 w-3" /> {a.filename}
              </a>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-end gap-6 shrink-0">
        {item.list_price != null && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.1em] text-slate-400">List</div>
            <div className={`mono-nums ${discounted ? "text-slate-400 line-through" : "text-slate-500"}`}>{fmtPrice(item.list_price)}</div>
          </div>
        )}
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.1em] text-blue-600 font-semibold">Your price</div>
          <div className="font-heading text-xl font-black text-slate-900 mono-nums">{fmtPrice(item.price)}</div>
        </div>
      </div>
    </div>
  );
}

export default function CatalogView({ catalog, fileUrl, onExport }) {
  const [activeFam, setActiveFam] = useState("All");
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const match = (it) => it.sku.toLowerCase().includes(term) || (it.description || "").toLowerCase().includes(term) || (it.notes || "").toLowerCase().includes(term);
  const shown = catalog.families
    .filter((g) => activeFam === "All" || g.family === activeFam)
    .map((g) => ({ ...g, items: term ? g.items.filter(match) : g.items }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      <ShippingNotice shipping={catalog.shipping} />
      <div className="relative max-w-md mb-5">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products by name, SKU or notes"
          className="w-full pl-9 pr-3 py-2.5 rounded-sm border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          data-testid="catalog-search" />
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="category-bar">
        {["All", ...catalog.families.map((g) => g.family)].map((name) => {
          const active = activeFam === name;
          return (
            <button key={name} onClick={() => setActiveFam(name)} data-testid={`cat-${name.replace(/\s+/g, "-")}`}
              className={`px-4 py-2 text-sm font-medium rounded-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
              {name}
            </button>
          );
        })}
      </div>

      {shown.length === 0 && (
        <div className="text-center py-24 text-slate-400">
          <PackageSearch className="h-12 w-12 mx-auto" />
          <p className="mt-4">{term ? `No products match "${q}".` : "No products are available for your pricing level yet."}</p>
        </div>
      )}

      {shown.map((group) => (
        <section key={group.family} className="mb-14" data-testid={`family-section-${group.family.replace(/\s+/g, "-")}`}>
          <div className="flex items-center justify-between border-b-2 border-slate-900 pb-2 mb-4">
            <h2 className="font-heading text-2xl font-black tracking-tight text-slate-900">{group.family}</h2>
            {onExport && (
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => onExport(group.family)} data-testid={`export-${group.family.replace(/\s+/g, "-")}`}>
                <Download className="h-4 w-4 mr-2" /> Export category
              </Button>
            )}
          </div>
          {[0, 1, 2].map((k) => {
            const items = group.items.filter((it) => it.kind === k);
            if (items.length === 0) return null;
            return (
              <div key={k} className="mb-5">
                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2" data-testid={`kind-label-${group.family.replace(/\s+/g, "-")}-${k}`}>{KIND_LABELS[k]}</div>
                <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
                  {items.map((item) => <ProductRow key={item.sku} item={item} fileUrl={fileUrl} />)}
                </div>
              </div>
            );
          })}
        </section>
      ))}
    </>
  );
}
